import Axios, { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { config } from "constant";
import { replaceSpecialChar } from "helpers";
import { cloneDeep } from "lodash";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

declare module "axios" {
  export interface AxiosRequestConfig {
    cache?: boolean;
  }
}
type Data = { [key: string]: any };
const data: Data = {};

export default function useAxios() {
  const axios = useMemo(
    () => Axios.create({ baseURL: `${config.baseUrl}/api` }),
    []
  );
  const controller = useMemo(() => new AbortController(), []);
  const [loading, setLoading] = useState<string[]>([]);
  const [error, setError] = useState<any>(null);

  const generateDataKey = (req: InternalAxiosRequestConfig) => {
    const key = replaceSpecialChar(
      `${req.url ?? ""}${JSON.stringify(req.params)}`
    );
    return key ?? "";
  };
  const loadingHandler = useCallback((value: boolean) => {
    setLoading((p) => {
      const loading = cloneDeep(p);
      value ? loading.push("") : loading.pop();
      return loading;
    });
  }, []);
  //   const progressHandler = (progressEvent: AxiosProgressEvent) => {
  //     const total = progressEvent.total ?? 1;
  //     const loaded = progressEvent.loaded;
  //     const percent = (loaded * 100) / total;
  //     const progress = Math.min(Math.round(percent), 100);
  //     setProgress(progress);
  //   };
  const errorsHandler = useCallback((err: any) => {
    // const detail = err?.response?.data?.detail;
    // const title = err?.response?.data?.title;
    // const message = err?.response?.data?.message;
    // const error = detail || title || message;
    // const status = err?.response?.status;
    // const isInvalid = status === 400;
    // const isNotFount = status === 404;
    // const isConflict = status === 409;
    // const isTeapot = status === 418;
    // const isServerError = status === 500;
    setError(err);
  }, []);
  const reqHandler = useCallback(
    async (req: InternalAxiosRequestConfig<any>) => {
      loadingHandler(true);
      setError(null);
      const isGet = req.method === "get";
      // const isPost = req.method === "post";
      // const isPut = req.method === "put";
      // const isPatch = req.method === "patch";
      // const cache = !!req.cache;
      const cache = true;
      const dataKey = generateDataKey(req);
      const hasData = data[dataKey];
      const needCache = [isGet, cache, hasData].every(Boolean);
      const adapterData = {
        config: req,
        request: req,
        headers: req.headers,
        data: data[dataKey],
        status: 200,
        statusText: "OK",
      };
      req.signal = controller.signal;
      if (needCache) {
        req.adapter = () => Promise.resolve(adapterData);
        return req;
      }
      return req;
    },
    [controller, loadingHandler]
  );
  const resHandler = useCallback(
    (res: AxiosResponse<any>) => {
      loadingHandler(false);
      setError(null);
      const key = generateDataKey(res.config);
      const value = res.data;
      data[key] = value;
      // resetRetryValue();
      return res;
    },
    [loadingHandler]
  );
  const errHandler = useCallback(
    async (err: any) => {
      loadingHandler(false);
      const isCanceled = err?.code === "ERR_CANCELED";
      const error = err?.config?.error ?? true;
      // const isNetworkError = err.code === "ERR_NETWORK";
      // if (isNetworkError) return axios.request(err.config);
      if (!isCanceled && error) errorsHandler(err);
      return Promise.reject(err);
    },
    [errorsHandler, loadingHandler]
  );
  useLayoutEffect(() => {
    const req = axios.interceptors.request.use(reqHandler);
    const res = axios.interceptors.response.use(resHandler, errHandler);
    return () => {
      axios.interceptors.request.eject(req);
      axios.interceptors.response.eject(res);
    };
  }, [reqHandler, resHandler, errHandler]);
  useEffect(() => {
    return () => {
      !config.isDevelopment && controller.abort();
    };
  }, [controller]);
  return [axios, !!loading.length, error] as const;
}
